import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Calendar, Clock, User, ArrowRight } from 'lucide-react';
import { buildBlogApiUrl } from '../../config/apiConfig';
import {
  blogArticlePath,
  blogBasePath,
  blogCategoryPath,
  type MarketingLocale,
} from '../../config/localeRoutes';
import { resolveBlogCategoryLabel } from '../../config/blogCategoryLabels';
import SEOHead from '../../components/SEO/SEOHead';
import { buildBreadcrumbSchema, buildBlogItemListSchema, absoluteCanonicalUrl, clipMetaDescription } from '../../components/SEO/schemas';
import BlogAllArticlesNav from './BlogAllArticlesNav';
import { fetchAllBlogArticles, type BlogArticleLink } from './fetchAllBlogArticles';
import './BlogPage.css';

interface Category {
    id: string;
    name: string;
    slug: string;
    icon: string;
    color: string;
    description?: string;
}

interface Article {
    id: string;
    title: string;
    slug: string;
    excerpt: string;
    featured_image_url: string;
    featured_image_alt: string;
    reading_time_minutes: number;
    published_at: string;
    tags: string[];
    category: Category | null;
    author_name: string;
}

interface ArticlesResponse {
    articles: Article[];
    total: number;
    page: number;
    pages: number;
}

interface CategoriesResponse {
    categories: Category[];
}

const BASE_URL = 'https://www.rintintin-pro.com';

function titleCaseFromSlug(slug: string): string {
    return slug
        .split('-')
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ');
}

function buildListPath(
    locale: MarketingLocale,
    opts: {
    categorySlug?: string;
    page?: number;
    tag?: string | null;
}): string {
    let path = opts.categorySlug
        ? blogCategoryPath(locale, opts.categorySlug)
        : blogBasePath(locale);
    const params = new URLSearchParams();
    if (opts.page && opts.page > 1) {
        params.set('page', String(opts.page));
    }
    if (opts.tag) {
        params.set('tag', opts.tag);
    }
    const qs = params.toString();
    return qs ? `${path}?${qs}` : path;
}

function getPaginationRange(current: number, total: number): (number | 'ellipsis')[] {
    if (total <= 7) {
        return Array.from({ length: total }, (_, i) => i + 1);
    }

    const pages: (number | 'ellipsis')[] = [1];

    if (current > 3) {
        pages.push('ellipsis');
    }

    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);

    for (let i = start; i <= end; i += 1) {
        pages.push(i);
    }

    if (current < total - 2) {
        pages.push('ellipsis');
    }

    pages.push(total);
    return pages;
}

type BlogPageProps = {
    locale?: MarketingLocale;
};

const BlogPage: React.FC<BlogPageProps> = ({ locale = 'fr' }) => {
    const { t, i18n } = useTranslation('marketing');
    const [searchParams, setSearchParams] = useSearchParams();
    const { categorySlug } = useParams<{ categorySlug?: string }>();

    const [articles, setArticles] = useState<Article[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState<string | null>(null);
    const [totalPages, setTotalPages] = useState(1);
    const [totalArticles, setTotalArticles] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [allArticlesNav, setAllArticlesNav] = useState<BlogArticleLink[]>([]);

    const page = parseInt(searchParams.get('page') || '1', 10);
    const tagFilter = searchParams.get('tag');

    const heroTagline = t('blogPage.hero.tagline');

    const categoryFromArticles = useMemo(() => {
        if (!categorySlug) return null;
        return articles.find((a) => a.category?.slug === categorySlug)?.category ?? null;
    }, [articles, categorySlug]);

    const categoryFromList = useMemo(
        () => categories.find((c) => c.slug === categorySlug) ?? null,
        [categories, categorySlug]
    );

    const activeCategory = categoryFromList ?? categoryFromArticles;

    const categoryDisplayName = categorySlug
        ? resolveBlogCategoryLabel(
              categorySlug,
              locale,
              activeCategory?.name ?? titleCaseFromSlug(categorySlug)
          )
        : '';

    const pageHeading = categorySlug ? categoryDisplayName : t('blogPage.hero.title');

    const seoTitle = categorySlug
        ? t('blogPage.seo.titleCategory', { category: categoryDisplayName })
        : tagFilter
            ? t('blogPage.seo.titleTag', { tag: tagFilter })
            : t('blogPage.seo.titleDefault');
    const pageWord =
        locale === 'es' ? 'página' : locale === 'de' ? 'Seite' : locale === 'it' ? 'pagina' : 'page';
    const documentTitle = page > 1 ? `${seoTitle} — ${pageWord} ${page}` : seoTitle;
    const visibleHeading = page > 1 ? `${pageHeading} — ${pageWord} ${page}` : pageHeading;
    const seoDescription = categorySlug
        ? activeCategory?.description || t('blogPage.seo.descriptionCategoryFallback')
        : heroTagline;
    const documentDescription = page > 1 ? `${seoDescription} — ${pageWord} ${page}` : seoDescription;
    const seoCanonicalPath = buildListPath(locale, {
        categorySlug,
        page: page > 1 ? page : undefined,
        tag: tagFilter,
    });

    const blogAlternatePaths = {
        fr: blogBasePath('fr'),
        es: blogBasePath('es'),
        en: blogBasePath('en'),
        de: blogBasePath('de'),
        it: blogBasePath('it'),
    };

    const listPageAbsoluteUrl = absoluteCanonicalUrl(seoCanonicalPath);

    const breadcrumbItems = [
        { name: t('blogPage.breadcrumb.home'), path: locale === 'fr' ? '/' : `/${locale}` },
        { name: t('blogPage.breadcrumb.blog'), path: blogBasePath(locale) },
        ...(categorySlug && categoryDisplayName
            ? [{ name: categoryDisplayName, path: blogCategoryPath(locale, categorySlug) }]
            : []),
    ];

    const blogSchema: Record<string, unknown> = {
        '@context': 'https://schema.org',
        '@type': 'Blog',
        name: t('blogPage.seo.schemaName'),
        description: heroTagline,
        url: absoluteCanonicalUrl(blogBasePath(locale)),
        inLanguage: locale,
        publisher: {
            '@type': 'Organization',
            name: 'Rintintin Pro',
            logo: { '@type': 'ImageObject', url: `${BASE_URL}/logo192.png` },
        },
    };

    const paginationSeoLinks = useMemo(() => {
        const links: React.ReactNode[] = [];
        if (currentPage > 1) {
            links.push(
                <link
                    key="prev"
                    rel="prev"
                    href={absoluteCanonicalUrl(buildListPath(locale, {
                        categorySlug,
                        page: currentPage - 1,
                        tag: tagFilter,
                    }))}
                />
            );
        }
        if (currentPage < totalPages) {
            links.push(
                <link
                    key="next"
                    rel="next"
                    href={absoluteCanonicalUrl(buildListPath(locale, {
                        categorySlug,
                        page: currentPage + 1,
                        tag: tagFilter,
                    }))}
                />
            );
        }
        return links;
    }, [categorySlug, currentPage, tagFilter, totalPages, locale]);

    useEffect(() => {
        fetchCategories();
    }, [locale]);

    useEffect(() => {
        fetchArticles();
    }, [page, categorySlug, tagFilter, locale]);

    useEffect(() => {
        if (page !== 1 || categorySlug || tagFilter) {
            setAllArticlesNav([]);
            return;
        }
        let cancelled = false;
        fetchAllBlogArticles(locale)
            .then((items) => {
                if (!cancelled) setAllArticlesNav(items);
            })
            .catch((error) => {
                console.error('Error fetching all blog articles:', error);
                if (!cancelled) setAllArticlesNav([]);
            });
        return () => {
            cancelled = true;
        };
    }, [page, categorySlug, tagFilter, locale]);

    const fetchCategories = async () => {
        try {
            const response = await axios.get<CategoriesResponse>(buildBlogApiUrl('/blog/categories'), {
                params: { site: 'pro', lang: locale },
            });
            setCategories(response.data.categories || []);
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    const fetchArticles = async () => {
        setLoading(true);
        setFetchError(null);
        try {
            const params: Record<string, string | number> = {
                page,
                limit: 9,
                site: 'pro',
                lang: locale,
            };
            if (categorySlug) {
                params.category = categorySlug;
            }
            if (tagFilter) {
                params.tag = tagFilter;
            }

            const response = await axios.get<ArticlesResponse>(buildBlogApiUrl('/blog/articles'), { params });
            setArticles(response.data.articles || []);
            setTotalPages(response.data.pages || 1);
            setTotalArticles(response.data.total || 0);
            setCurrentPage(response.data.page || 1);
        } catch (error) {
            console.error('Error fetching articles:', error);
            setArticles([]);
            if (axios.isAxiosError(error)) {
                const st = error.response?.status;
                const detail =
                    (error.response?.data as { error?: string } | undefined)?.error ||
                    error.message;
                setFetchError(
                    st
                        ? t('blogPage.errors.fetchWithStatus', {
                              status: st,
                              detail: detail || '',
                          }).trim()
                        : t('blogPage.errors.fetchGeneric')
                );
            } else {
                setFetchError(t('blogPage.errors.fetchGeneric'));
            }
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString(i18n.language, {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        });
    };

    const handlePageChange = (newPage: number) => {
        const params: Record<string, string> = { page: newPage.toString() };
        if (tagFilter) {
            params.tag = tagFilter;
        }
        setSearchParams(params);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const clearTagFilter = () => {
        const params: Record<string, string> = {};
        if (page > 1) {
            params.page = page.toString();
        }
        setSearchParams(params);
    };

    const paginationRange = getPaginationRange(currentPage, totalPages);
    const isProduction = process.env.NODE_ENV === 'production';

    return (
        <>
            <SEOHead
                title={documentTitle}
                description={documentDescription}
                canonicalPath={seoCanonicalPath}
                alternatePaths={
                    !categorySlug && !tagFilter && page <= 1 ? blogAlternatePaths : undefined
                }
                noindex={Boolean(tagFilter)}
                keywords={t('blogPage.seo.keywords')}
                schemaData={[
                    buildBreadcrumbSchema(breadcrumbItems),
                    ...(!categorySlug && !tagFilter && page <= 1 ? [blogSchema] : []),
                    ...(articles.length > 0
                        ? [
                              buildBlogItemListSchema(
                                  articles.map((a) => ({
                                      title: a.title,
                                      slug: a.slug,
                                      datePublished: a.published_at,
                                  })),
                                  listPageAbsoluteUrl,
                                  blogBasePath(locale),
                                  t('blogPage.seo.schemaName'),
                                  locale,
                                  page > 1 ? (page - 1) * 9 + 1 : 1,
                                  totalArticles || undefined
                              ),
                          ]
                        : []),
                ]}
            >
                {paginationSeoLinks}
            </SEOHead>
            <div className="blog-page">
                <section className="blog-hero blog-hero--under-fixed-nav">
                    <div className="blog-container">
                        <div className="blog-hero-content">
                            <nav className="blog-breadcrumb" aria-label={t('blogPage.breadcrumb.ariaLabel')}>
                                <Link to={locale === 'fr' ? '/' : `/${locale}`} className="breadcrumb-link">
                                    {t('blogPage.breadcrumb.home')}
                                </Link>
                                <span>/</span>
                                {categorySlug ? (
                                    <>
                                        <Link to={blogBasePath(locale)} className="breadcrumb-link">
                                            {t('blogPage.breadcrumb.blog')}
                                        </Link>
                                        <span>/</span>
                                        <span className="breadcrumb-current">{categoryDisplayName}</span>
                                    </>
                                ) : (
                                    <span className="breadcrumb-current">{t('blogPage.breadcrumb.blog')}</span>
                                )}
                            </nav>

                            <h1 className="blog-title">{visibleHeading}</h1>
                            <p className="blog-subtitle">
                                {categorySlug && activeCategory?.description
                                    ? activeCategory.description
                                    : heroTagline}
                            </p>
                        </div>
                    </div>
                </section>

                {tagFilter ? (
                    <div className="blog-tag-filter">
                        <div className="blog-container">
                            <p className="blog-tag-filter-text">
                                {t('blogPage.tagFilter.label')} <strong>{tagFilter}</strong>
                            </p>
                            <button type="button" className="blog-tag-filter-clear" onClick={clearTagFilter}>
                                {t('blogPage.tagFilter.clearAll')}
                            </button>
                        </div>
                    </div>
                ) : null}

                <section className="blog-articles">
                    <div className="blog-container">
                        {loading ? (
                            <div className="blog-loading">
                                <div className="loading-spinner"></div>
                                <p>{t('blogPage.loading')}</p>
                            </div>
                        ) : fetchError ? (
                            <div className="blog-fetch-error" role="alert">
                                <h2>{t('blogPage.errors.title')}</h2>
                                <p>{fetchError}</p>
                                {!isProduction ? (
                                    <p className="blog-fetch-error-hint">
                                        {t('blogPage.errors.devHint', { code: 'REACT_APP_API_BASE_URL' })}
                                    </p>
                                ) : null}
                            </div>
                        ) : articles.length === 0 ? (
                            <div className="blog-empty">
                                <span className="empty-icon">📝</span>
                                <h2>{t('blogPage.empty.title')}</h2>
                                <p>{t('blogPage.empty.description')}</p>
                            </div>
                        ) : (
                            <>
                                <ul className="blog-list" role="list">
                                    {articles.map((article, index) => {
                                        const isFeatured = currentPage === 1 && index === 0 && !tagFilter;
                                        const hasImage = Boolean(article.featured_image_url);
                                        const cardClass = [
                                            'blog-card',
                                            hasImage ? '' : 'blog-card--no-image',
                                            isFeatured ? 'blog-card--featured' : '',
                                        ]
                                            .filter(Boolean)
                                            .join(' ');
                                        const articleHref = blogArticlePath(locale, article.slug);
                                        const articleAriaLabel = t('blogPage.article.readAriaLabel', {
                                            title: article.title,
                                        });
                                        const cardBody = (
                                            <>
                                                {article.author_name ? (
                                                    <p className="blog-card-author">
                                                        <User size={14} strokeWidth={2.25} aria-hidden />
                                                        <span>{article.author_name}</span>
                                                    </p>
                                                ) : null}
                                                <h2 className="blog-card-title">{article.title}</h2>
                                                {article.excerpt ? (
                                                    <p className="blog-card-excerpt">{clipMetaDescription(article.excerpt)}</p>
                                                ) : null}

                                                <div className="blog-card-footer">
                                                    <div className="blog-card-footer-row">
                                                        <div className="blog-card-meta">
                                                            <time
                                                                className="blog-meta-item blog-meta-item--time"
                                                                dateTime={article.published_at}
                                                            >
                                                                <Calendar size={14} strokeWidth={2} aria-hidden />
                                                                {formatDate(article.published_at)}
                                                            </time>
                                                            {article.reading_time_minutes ? (
                                                                <span className="blog-meta-item">
                                                                    <Clock size={14} strokeWidth={2} aria-hidden />
                                                                    {t('blogPage.article.readingTime', {
                                                                        minutes: article.reading_time_minutes,
                                                                    })}
                                                                </span>
                                                            ) : null}
                                                        </div>
                                                        <span className="blog-card-cta">
                                                            {t('blogPage.article.readCta')}
                                                            <ArrowRight size={16} strokeWidth={2.25} aria-hidden />
                                                        </span>
                                                    </div>
                                                </div>
                                            </>
                                        );
                                        return (
                                            <li
                                                key={article.id}
                                                className={
                                                    isFeatured
                                                        ? 'blog-list__item blog-list__item--featured'
                                                        : 'blog-list__item'
                                                }
                                            >
                                                <div className="blog-card-outer">
                                                    {hasImage && article.category ? (
                                                        <Link
                                                            to={blogCategoryPath(locale, article.category.slug)}
                                                            className="blog-card-category blog-card-category--overlay"
                                                        >
                                                            {article.category.name}
                                                        </Link>
                                                    ) : null}
                                                    {hasImage ? (
                                                        <Link
                                                            to={articleHref}
                                                            className={cardClass}
                                                            aria-label={articleAriaLabel}
                                                        >
                                                            <div className="blog-card-image-wrapper">
                                                                <img
                                                                    src={article.featured_image_url!}
                                                                    alt={
                                                                        article.featured_image_alt || article.title
                                                                    }
                                                                    className="blog-card-image"
                                                                    loading={index < 3 ? 'eager' : 'lazy'}
                                                                />
                                                            </div>
                                                            <div className="blog-card-content">{cardBody}</div>
                                                        </Link>
                                                    ) : (
                                                        <div className={cardClass}>
                                                            <div className="blog-card-content">
                                                                {article.category ? (
                                                                    <Link
                                                                        to={blogCategoryPath(locale, article.category.slug)}
                                                                        className="blog-card-category"
                                                                    >
                                                                        {article.category.name}
                                                                    </Link>
                                                                ) : null}
                                                                <Link
                                                                    to={articleHref}
                                                                    className="blog-card-surface"
                                                                    aria-label={articleAriaLabel}
                                                                >
                                                                    {cardBody}
                                                                </Link>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>

                                {totalPages > 1 ? (
                                    <nav className="blog-pagination" aria-label={t('blogPage.pagination.ariaLabel')}>
                                        <button
                                            type="button"
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                            className="pagination-btn"
                                            aria-label={t('blogPage.pagination.previousPage')}
                                        >
                                            {t('blogPage.pagination.previous')}
                                        </button>

                                        <span className="pagination-status" aria-live="polite">
                                            {t('blogPage.pagination.status', {
                                                current: currentPage,
                                                total: totalPages,
                                            })}
                                        </span>

                                        <div className="pagination-pages">
                                            {paginationRange.map((pageNum, idx) =>
                                                pageNum === 'ellipsis' ? (
                                                    <span key={`ellipsis-${idx}`} className="pagination-ellipsis">
                                                        …
                                                    </span>
                                                ) : (
                                                    <button
                                                        key={pageNum}
                                                        type="button"
                                                        onClick={() => handlePageChange(pageNum)}
                                                        className={`pagination-page ${
                                                            pageNum === currentPage ? 'active' : ''
                                                        }`}
                                                        aria-current={pageNum === currentPage ? 'page' : undefined}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                )
                                            )}
                                        </div>

                                        <button
                                            type="button"
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                            className="pagination-btn"
                                            aria-label={t('blogPage.pagination.nextPage')}
                                        >
                                            {t('blogPage.pagination.next')}
                                        </button>
                                    </nav>
                                ) : null}

                                {page === 1 && !categorySlug && !tagFilter ? (
                                    <BlogAllArticlesNav locale={locale} articles={allArticlesNav} />
                                ) : null}
                            </>
                        )}
                    </div>
                </section>
            </div>
        </>
    );
};

export default BlogPage;
